import _ from 'underscore';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  useCustomerLevelVariables,
  useAccountLevelVariables,
  usePostponeReadVariables,
  usePilotsValue,
} from '@bingads-webui-campaign-react/basic-context-utils';
import { getTimezoneI18nString } from '@bingads-webui-campaign-react/help';
import { CurrencyUtils } from '@bingads-webui-clientcenter/entity-utils';
import { usePromisify, FetchPolicy } from '@bingads-webui-react/overreact';
import {
  useCountries,
  useAccount,
  useTimeZones,
  useCurrencyCountriesMappings,
  useCurrencies,
  useSubGeographies,
  useCountryTimeZonesMapping,
} from '@bingads-webui/overreact-cc';

import { useGetSupportedCountryList } from './use-get-supported-country-list';

function convertTimeZonesForCcForm(i18n, timeZones) {
  return _.map(timeZones, item => ({
    Value: item.id,
    Text: i18n.getString(getTimezoneI18nString(item.id)),
  }));
}

export const useLoadAccountInfo = ({ countryCode, timezonePrefillEnabled }) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);

  const customerLevelVariables = useCustomerLevelVariables();
  const accountLevelVariables = useAccountLevelVariables();
  const postponeReadConfig = usePostponeReadVariables();
  const isGoogleAuthIntlNewSignInPageEnabled = usePilotsValue('GoogleAuthIntlNewSignInPage');

  const [{ data: countries }] = useCountries(customerLevelVariables);
  const [{ data: account }] = useAccount(accountLevelVariables);
  const [{ data: tZones }] = useTimeZones(customerLevelVariables);
  const [{ data: currencyCountriesMappings }] = useCurrencyCountriesMappings(customerLevelVariables);
  const [{ data: currencies }] = useCurrencies(customerLevelVariables);
  const [{ data: countryTimeZonesMapping }, { read: readCountryTimeZonesMapping }]
    = useCountryTimeZonesMapping(customerLevelVariables, postponeReadConfig);
  const [{ data: statesOrProvinces }, { read: readStatesOrProvinces }]
    = useSubGeographies(customerLevelVariables, postponeReadConfig);
  const [readStatesOrProvincesAsync, readCountryTimeZonesMappingAsync]
    = usePromisify([readStatesOrProvinces, readCountryTimeZonesMapping]);

  const { supportedCountryList, getSupportedCountryList } = useGetSupportedCountryList();

  const isFetchingSupportedCountryList = useRef(false);
  const isFetchingStatesOrProvinces = useRef(false);
  const isFetchingCountryTimeZonesMapping = useRef(false);

  const isAccountInfoLoaded = account && countries && tZones
    && currencyCountriesMappings && currencies && statesOrProvinces
    && (!timezonePrefillEnabled || (timezonePrefillEnabled && countryTimeZonesMapping));

  const prefillTimezone = _.isEmpty(countryTimeZonesMapping) ? undefined :
    _.first(_.get(_.findWhere(countryTimeZonesMapping, { country: countryCode }), 'timeZones'));

  const preferredTimeZoneCode = timezonePrefillEnabled ? prefillTimezone : _.get(account, ['TimeZone']);

  const timeZones = useMemo(() => (
    convertTimeZonesForCcForm(i18n, tZones)
  ), [i18n, tZones]);

  const sortedCurrencies = useMemo(() => {
    let allowedCurrencies = _.pluck(currencies, 'value');
    if (['Google', 'Facebook'].includes((serverContext as any).AuthType)) {
      if (isGoogleAuthIntlNewSignInPageEnabled) {
        allowedCurrencies = _.filter(allowedCurrencies, currency => !['CNY', 'INR'].includes(currency.Code));
      } else {
        allowedCurrencies = _.filter(allowedCurrencies, currency => currency.Code === 'USD');
      }
    }
    const sorted = CurrencyUtils.sortCurrencies(allowedCurrencies);
    return sorted;
  }, [currencies, (serverContext as any).AuthType,
    isGoogleAuthIntlNewSignInPageEnabled]);

  useEffect(() => {
    if (_.isEmpty(supportedCountryList) && countries && currencyCountriesMappings
      && !isFetchingSupportedCountryList.current) {
      isFetchingSupportedCountryList.current = true;
      getSupportedCountryList({ countries, currencyCountriesMappings }).finally(() => {
        isFetchingSupportedCountryList.current = false;
      });
    }
  }, [getSupportedCountryList, supportedCountryList, countries, currencyCountriesMappings]);

  useEffect(() => {
    if (_.isEmpty(statesOrProvinces) && countryCode && !isFetchingStatesOrProvinces.current && account) {
      isFetchingStatesOrProvinces.current = true;
      readStatesOrProvincesAsync({
        variables: {
          countryCode,
          options: {
            fetchPolicy: FetchPolicy.NetworkOnly,
          },
        },
      }).finally(() => {
        isFetchingStatesOrProvinces.current = false;
      });
    }
  }, [statesOrProvinces, readStatesOrProvincesAsync, countryCode, account]);

  useEffect(() => {
    if (timezonePrefillEnabled && _.isEmpty(countryTimeZonesMapping) && !isFetchingCountryTimeZonesMapping.current) {
      isFetchingCountryTimeZonesMapping.current = true;
      readCountryTimeZonesMappingAsync().finally(() => {
        isFetchingCountryTimeZonesMapping.current = false;
      });
    }
  }, [countryTimeZonesMapping, readCountryTimeZonesMappingAsync, timezonePrefillEnabled]);

  return {
    account,
    countries,
    timeZones,
    currencies: sortedCurrencies,
    currencyCountriesMappings,
    preferredTimeZoneCode,
    statesOrProvinces,
    supportedCountryList,
    isAccountInfoLoaded,
  };
};
